import { Alert } from "@tournated-com/ui";
import { useEffect } from "react";
import { useRouteError } from "react-router-dom";

import { useT } from "@/i18n";
import { captureSentryException } from "@/lib/sentry";
import { isDynamicImportError, reloadForStaleChunkOnce } from "@/utils/common";

import { DynamicImportErrorPage } from "./DynamicImportErrorPage";

export const RouteErrorBoundary = () => {
  const error = useRouteError();

  const isStaleChunk = isDynamicImportError(error);

  // Log error in development
  if (import.meta.env.DEV) {
    console.error("RouteErrorBoundary caught an error:", error);
  }

  useEffect(() => {
    if (!error) return;
    captureSentryException(error, {
      tags: { source: "react-router", dynamicImport: isStaleChunk },
    });
  }, [error, isStaleChunk]);

  // A stale chunk means a new version was deployed while this tab was open. Try a
  // single automatic reload to pull the fresh chunks; only fall back to the
  // refresh page if the reload was throttled (it already ran very recently).
  if (isStaleChunk) {
    if (reloadForStaleChunkOnce()) {
      return null;
    }
    return <DynamicImportErrorPage />;
  }

  return <UnknownError />;
};

export const UnknownError = () => {
  const t = useT();
  const error = useRouteError() as { message: string };

  return (
    <Alert
      variant="danger"
      title={t("general.errorState.technical.title")}
      description={t("general.errorState.technical.description", { error: error.message })}
    />
  );
};
